import React, { useEffect } from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import type { SilaeFormData } from "@/types/silae";
import type { FormikProps } from "formik";
import { useTranslation } from "react-i18next";
import { getIn } from "formik";
import { SilaeDatePicker } from "./SilaeDatePicker";
import { CUMUL_STOCK_VAR_PERIODS } from "@/constants/code-generation";
import {
  FormikErrorMessage,
  useFormikFieldError,
} from "@/components/FormikErrorMessage";

interface CumulStockVarConfigProps {
  formik: FormikProps<SilaeFormData>;
  fieldPrefix: string; // Ex: "ifBranch.bonuses.0.bases.0" (source) ou "conditions.0" (condition)
}

/**
 * CumulStockVarConfig - Composant unifié pour sources ET conditions
 *
 * Ce composant fonctionne dans les deux contextes (sources et conditions)
 * car les noms de champs sont identiques dans les deux cas.
 *
 * Utilise uniquement:
 * - formik: Pour accéder et mettre à jour les valeurs
 * - fieldPrefix: Chemin complet vers l'objet (source ou condition)
 */
export const CumulStockVarConfig: React.FC<CumulStockVarConfigProps> = ({
  formik,
  fieldPrefix,
}) => {
  const { t } = useTranslation();

  // Accéder aux valeurs via getIn
  const cumulStockVarName = getIn(
    formik.values,
    `${fieldPrefix}.cumulStockVarName`,
  ) as string | undefined;
  const cumulStockVarPeriod = getIn(
    formik.values,
    `${fieldPrefix}.cumulStockVarPeriod`,
  ) as string | undefined;
  const cumulStockVarDateDebut = getIn(
    formik.values,
    `${fieldPrefix}.cumulStockVarDateDebut`,
  ) as string | undefined;
  const cumulStockVarDateFin = getIn(
    formik.values,
    `${fieldPrefix}.cumulStockVarDateFin`,
  ) as string | undefined;
  const cumulStockVarPercentage = getIn(
    formik.values,
    `${fieldPrefix}.cumulStockVarPercentage`,
  ) as string | undefined;
  const cumulStockVarEnableAdditional = getIn(
    formik.values,
    `${fieldPrefix}.cumulStockVarEnableAdditional`,
  ) as boolean | undefined;
  const cumulStockVarAdditionalOperator = getIn(
    formik.values,
    `${fieldPrefix}.cumulStockVarAdditionalOperator`,
  ) as string | undefined;
  const cumulStockVarAdditionalValue = getIn(
    formik.values,
    `${fieldPrefix}.cumulStockVarAdditionalValue`,
  ) as string | undefined;

  // Récupérer les erreurs et classes d'erreur pour chaque champ
  const nameError = useFormikFieldError(
    formik,
    `${fieldPrefix}.cumulStockVarName`,
  );
  const periodError = useFormikFieldError(
    formik,
    `${fieldPrefix}.cumulStockVarPeriod`,
  );
  const percentageError = useFormikFieldError(
    formik,
    `${fieldPrefix}.cumulStockVarPercentage`,
  );
  const additionalOperatorError = useFormikFieldError(
    formik,
    `${fieldPrefix}.cumulStockVarAdditionalOperator`,
  );
  const additionalValueError = useFormikFieldError(
    formik,
    `${fieldPrefix}.cumulStockVarAdditionalValue`,
  );

  /**
   * Gère le changement de période avec mise à jour automatique des dates
   */
  const handlePeriodChange = (periodKey: string) => {
    formik.setFieldValue(`${fieldPrefix}.cumulStockVarPeriod`, periodKey);

    if (periodKey !== "custom") {
      const period =
        CUMUL_STOCK_VAR_PERIODS[
          periodKey as keyof typeof CUMUL_STOCK_VAR_PERIODS
        ];
      if (period) {
        formik.setFieldValue(
          `${fieldPrefix}.cumulStockVarDateDebut`,
          period.debut,
        );
        formik.setFieldValue(`${fieldPrefix}.cumulStockVarDateFin`, period.fin);
      }
    } else {
      // Réinitialiser les dates si on passe en mode personnalisé
      formik.setFieldValue(`${fieldPrefix}.cumulStockVarDateDebut`, "");
      formik.setFieldValue(`${fieldPrefix}.cumulStockVarDateFin`, "");
    }
  };

  // Initialisation des dates au montage si nécessaire
  useEffect(() => {
    const currentPeriod = cumulStockVarPeriod || "year_current";

    // Initialiser uniquement si les deux dates sont vides et la période n'est pas "custom"
    if (
      !cumulStockVarDateDebut &&
      !cumulStockVarDateFin &&
      currentPeriod !== "custom"
    ) {
      const period =
        CUMUL_STOCK_VAR_PERIODS[
          currentPeriod as keyof typeof CUMUL_STOCK_VAR_PERIODS
        ];
      if (period && period.debut && period.fin) {
        formik.setFieldValue(
          `${fieldPrefix}.cumulStockVarDateDebut`,
          period.debut,
        );
        formik.setFieldValue(`${fieldPrefix}.cumulStockVarDateFin`, period.fin);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Exécuter uniquement au montage

  /**
   * Gère le changement de l'option "Valeur supplémentaire"
   */
  const handleAdditionalToggle = (enabled: boolean) => {
    formik.setFieldValue(
      `${fieldPrefix}.cumulStockVarEnableAdditional`,
      enabled,
    );
    // Réinitialiser les valeurs si désactivé
    if (!enabled) {
      formik.setFieldValue(
        `${fieldPrefix}.cumulStockVarAdditionalOperator`,
        "+",
      );
      formik.setFieldValue(`${fieldPrefix}.cumulStockVarAdditionalValue`, "0");
    }
  };

  const currentPeriod = cumulStockVarPeriod || "year_current";
  const isCustomPeriod = currentPeriod === "custom";
  const isAdditionalEnabled = cumulStockVarEnableAdditional || false;

  return (
    <div className="space-y-4 border rounded-lg p-4 bg-blue-50 dark:bg-blue-950/20">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
          {t("cumulStockVarConfig.title")}
        </span>
      </div>

      {/* Nom de la variable stockée */}
      <div className="space-y-2">
        <Label>{t("cumulStockVarConfig.name.label")}</Label>
        <Input
          type="text"
          placeholder={t("cumulStockVarConfig.name.placeholder")}
          value={cumulStockVarName || ""}
          onChange={(e) => {
            formik.setFieldValue(
              `${fieldPrefix}.cumulStockVarName`,
              e.target.value.toUpperCase(),
            );
          }}
          onBlur={() => {
            formik.setFieldTouched(`${fieldPrefix}.cumulStockVarName`, true);
          }}
          className={nameError.errorClass}
        />
        <FormikErrorMessage
          formik={formik as unknown as FormikProps<Record<string, unknown>>}
          name={`${fieldPrefix}.cumulStockVarName`}
        />
      </div>

      {/* Période de cumul */}
      <div className="space-y-2">
        <Label>{t("cumulStockVarConfig.period.label")}</Label>
        <Select value={currentPeriod} onValueChange={handlePeriodChange}>
          <SelectTrigger className={periodError.errorClass}>
            <SelectValue
              placeholder={t("cumulStockVarConfig.period.placeholder")}
            />
          </SelectTrigger>
          <SelectContent>
            {Object.values(CUMUL_STOCK_VAR_PERIODS).map((period) => (
              <SelectItem key={period.key} value={period.key}>
                {period.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <FormikErrorMessage
          formik={formik as unknown as FormikProps<Record<string, unknown>>}
          name={`${fieldPrefix}.cumulStockVarPeriod`}
        />
      </div>

      {/* Dates personnalisées */}
      {isCustomPeriod && (
        <div className="flex flex-wrap gap-4">
          <div className="space-y-2 flex-1">
            <SilaeDatePicker
              value={cumulStockVarDateDebut || ""}
              onChange={(value) => {
                formik.setFieldValue(
                  `${fieldPrefix}.cumulStockVarDateDebut`,
                  value,
                );
              }}
              label={t("cumulStockVarConfig.customDates.startDate.label")}
              allowSilaeDateVariables={true}
            />
            <FormikErrorMessage
              formik={formik as unknown as FormikProps<Record<string, unknown>>}
              name={`${fieldPrefix}.cumulStockVarDateDebut`}
            />
          </div>
          <div className="space-y-2 flex-1">
            <SilaeDatePicker
              value={cumulStockVarDateFin || ""}
              onChange={(value) => {
                formik.setFieldValue(
                  `${fieldPrefix}.cumulStockVarDateFin`,
                  value,
                );
              }}
              label={t("cumulStockVarConfig.customDates.endDate.label")}
              allowSilaeDateVariables={true}
            />
            <FormikErrorMessage
              formik={formik as unknown as FormikProps<Record<string, unknown>>}
              name={`${fieldPrefix}.cumulStockVarDateFin`}
            />
          </div>
        </div>
      )}

      {/* Pourcentage */}
      <div className="space-y-2">
        <Label>{t("cumulStockVarConfig.percentage.label")}</Label>
        <Input
          type="number"
          min="0"
          max="100"
          placeholder={t("cumulStockVarConfig.percentage.placeholder")}
          value={cumulStockVarPercentage || ""}
          onChange={(e) => {
            formik.setFieldValue(
              `${fieldPrefix}.cumulStockVarPercentage`,
              e.target.value,
            );
          }}
          onBlur={() => {
            formik.setFieldTouched(
              `${fieldPrefix}.cumulStockVarPercentage`,
              true,
            );
          }}
          className={percentageError.errorClass}
        />
        <FormikErrorMessage
          formik={formik as unknown as FormikProps<Record<string, unknown>>}
          name={`${fieldPrefix}.cumulStockVarPercentage`}
        />
        <p className="text-xs text-blue-600 dark:text-blue-400">
          💡 {t("cumulStockVarConfig.percentage.helpText")}
        </p>
      </div>

      {/* Valeur supplémentaire */}
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Switch
            id={`cumulStockVarEnableAdditional-${fieldPrefix}`}
            checked={isAdditionalEnabled}
            onCheckedChange={handleAdditionalToggle}
          />
          <Label htmlFor={`cumulStockVarEnableAdditional-${fieldPrefix}`}>
            {t("cumulStockVarConfig.additional.enable")}
          </Label>
        </div>

        {isAdditionalEnabled && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>
                {t("cumulStockVarConfig.additional.operator.label")}
              </Label>
              <Select
                value={cumulStockVarAdditionalOperator || "+"}
                onValueChange={(value) => {
                  formik.setFieldValue(
                    `${fieldPrefix}.cumulStockVarAdditionalOperator`,
                    value,
                  );
                }}
              >
                <SelectTrigger className={additionalOperatorError.errorClass}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="+">
                    {t("cumulStockVarConfig.additional.operator.add")}
                  </SelectItem>
                  <SelectItem value="-">
                    {t("cumulStockVarConfig.additional.operator.subtract")}
                  </SelectItem>
                  <SelectItem value="*">
                    {t("cumulStockVarConfig.additional.operator.multiply")}
                  </SelectItem>
                  <SelectItem value="/">
                    {t("cumulStockVarConfig.additional.operator.divide")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("cumulStockVarConfig.additional.value.label")}</Label>
              <Input
                type="number"
                placeholder={t(
                  "cumulStockVarConfig.additional.value.placeholder",
                )}
                value={cumulStockVarAdditionalValue || ""}
                onChange={(e) => {
                  formik.setFieldValue(
                    `${fieldPrefix}.cumulStockVarAdditionalValue`,
                    e.target.value,
                  );
                }}
                onBlur={() => {
                  formik.setFieldTouched(
                    `${fieldPrefix}.cumulStockVarAdditionalValue`,
                    true,
                  );
                }}
                className={additionalValueError.errorClass}
              />
              <FormikErrorMessage
                formik={
                  formik as unknown as FormikProps<Record<string, unknown>>
                }
                name={`${fieldPrefix}.cumulStockVarAdditionalValue`}
              />
            </div>
          </div>
        )}
      </div>

    </div>
  );
};
